import { FormEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Divider, Flex, Form, Input, Typography } from 'antd';
import { StyledServiceRequestFormContent, StyledServiceRequestFormPage } from './ServiceRequestForm.styled';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { AuthenticationActions } from 'app/cross-cutting-concerns/authentication/state/authenticationActions';
import { setIsLoggedFromService } from 'app/modules/service/utils/serviceLocalStorage';
import { EMAIL_REGEX } from 'app/modules/service/utils/constants';
import { RoutePaths } from 'config/route-paths';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { useServiceRequestContext } from 'app/modules/service/context/ServiceContext';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';

export const ServiceRequestForm = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { handleEmail, handleServiceNext } = useServiceRequestContext();

  const [form] = Form.useForm();
  const emailValue = Form.useWatch('email', form);

  const isValidEmail = emailValue && EMAIL_REGEX.test(emailValue);
  const canSendCode = !!isValidEmail;

  const validateMessage = {
    types: {
      email: (
        <Flex align="center" gap={6}>
          <SvgIcon name="warningCritical" />
          {t('serviceRequestPage.form.emailInvalid')}
        </Flex>
      ) as unknown as string,
    },
  };

  const onFinish = (values: { email: string }): void => {
    handleEmail(values.email);
    handleServiceNext();
  };

  const onLoginWithKarcherAccount = (): void => {
    setIsLoggedFromService();
    dispatch(AuthenticationActions.signIn());
  };

  const handleInput = (event: FormEvent<HTMLInputElement>): void => {
    const value = event.currentTarget.value;

    const normalizedEmail = value.replace(/\s/g, '');

    if (normalizedEmail.length === 0) {
      form.setFieldValue('email', undefined);
      form.setFields([{ name: 'email', errors: [] }]);
    }
  };

  return (
    <StyledServiceRequestFormPage>
      <StyledServiceRequestFormContent vertical gap={24}>
        <Flex vertical justify="start" align="start" gap={8}>
          <Typography.Title
            level={3}
            style={{ margin: 0, fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD }}
          >
            {t('serviceRequestPage.form.title')}
          </Typography.Title>

          <Typography.Text style={{ margin: 0, fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.REGULAR }}>
            {t('serviceRequestPage.form.description')}
          </Typography.Text>
        </Flex>

        <Flex vertical gap={16} style={{ width: '100%' }}>
          <Form
            form={form}
            onFinish={onFinish}
            autoComplete="off"
            validateMessages={validateMessage}
            layout="vertical"
            style={{ margin: 0 }}
          >
            <Form.Item
              name="email"
              label={t('serviceRequestPage.form.emailLabel')}
              rules={[{ type: 'email' }]}
              style={{ marginBottom: 46 }}
            >
              <Input autoFocus onInput={handleInput} />
            </Form.Item>

            <Form.Item label={null} style={{ marginBottom: 0 }}>
              <Flex vertical gap={8} justify="center" align="center">
                <KccButton
                  type="primary"
                  size="middle"
                  htmlType="submit"
                  className="send-code-button"
                  style={{
                    width: '100%',
                    height: kccThemeConstants.CONTROL.HEIGHT_LG,
                    fontSize: kccThemeConstants.TYPOGRAPHY.FONT_SIZE.XL,
                  }}
                  disabled={!canSendCode}
                >
                  {t('serviceRequestPage.form.sendCode').toLowerCase()}
                </KccButton>

                <Typography.Text style={{ margin: 0 }}>
                  <Trans
                    i18nKey="serviceRequestPage.form.termsAgreement"
                    components={{
                      termsLink: (
                        <Typography.Link
                          href={RoutePaths.CONTRACT_OBLIGATION}
                          target="_blank"
                          underline
                          style={{
                            color: kccThemeConstants.COLORS.TEXT,
                            fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.REGULAR,
                            fontSize: kccThemeConstants.TYPOGRAPHY.FONT_SIZE.SM,
                          }}
                        />
                      ),
                    }}
                  />
                </Typography.Text>
              </Flex>
            </Form.Item>
          </Form>
        </Flex>

        <Divider style={{ margin: 0 }}>
          <Typography.Text style={{ margin: 0, fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.REGULAR }}>
            {t('serviceRequestPage.form.dividerOr')}
          </Typography.Text>
        </Divider>

        <KccButton type="secondary" size="middle" style={{ width: '100%' }} onClick={onLoginWithKarcherAccount}>
          {t('serviceRequestPage.form.loginWithKarcher')}
        </KccButton>
      </StyledServiceRequestFormContent>
    </StyledServiceRequestFormPage>
  );
};
